import { useState } from 'react';
import { useConfirmDeal, useRefuseDeal } from '@/hooks/useDeal';
import { Button } from '@/components/ui/Button';
import { haptic } from '@/lib/telegram';
import type { AuctionDetail } from '@/types/domain';
import styles from './DealPanel.module.css';

interface Props {
  auction: AuctionDetail;
  myUserId: string;
}

export const DealPanel = ({ auction, myUserId }: Props) => {
  const confirm = useConfirmDeal(auction.id);
  const refuse = useRefuseDeal(auction.id);
  const [showRefuse, setShowRefuse] = useState(false);
  const [refuseText, setRefuseText] = useState('');

  // Кто я в этой сделке?
  const isSeller = auction.seller.id === myUserId;
  const isWinner = auction.current_leader_id === myUserId;

  // Панель — только для участников завершённого лота
  if (auction.status !== 'finished') return null;
  if (!isSeller && !isWinner) return null;

  const myConfirmed = isSeller
    ? auction.seller_confirmed_at
    : auction.winner_confirmed_at;
  const otherConfirmed = isSeller
    ? auction.winner_confirmed_at
    : auction.seller_confirmed_at;

  const otherPartyLabel = isSeller ? 'покупатель' : 'продавец';

  const handleConfirm = () => {
    haptic.medium();
    confirm.mutate();
  };

  const handleRefuse = () => {
    haptic.warning();
    refuse.mutate(refuseText.trim() || null, {
      onSuccess: () => setShowRefuse(false),
    });
  };

  // Уже подтвердил — показываем статус ожидания.
  // Если вторая сторона ещё НЕ подтвердила, оставляем возможность пожаловаться:
  // контрагент может пропасть или отказаться передавать лот уже после нашего подтверждения.
  if (myConfirmed) {
    return (
      <div className={styles.panel}>
        <div className={styles.statusRow}>
          <CheckIcon />
          <div>
            <p className={styles.statusTitle}>Ты подтвердил сделку</p>
            <p className={styles.statusSub}>
              {otherConfirmed
                ? 'Сделка завершена обеими сторонами'
                : `Ждём, пока ${otherPartyLabel} подтвердит`}
            </p>
          </div>
        </div>

        {!otherConfirmed && (
          !showRefuse ? (
            <button
              className={styles.refuseLink}
              onClick={() => { haptic.light(); setShowRefuse(true); }}
            >
              Проблема со сделкой
            </button>
          ) : (
            <div className={styles.refuseBox}>
              <p className={styles.refuseWarning}>
                Если {otherPartyLabel} не выполнил условия — опиши проблему. Жалоба уйдёт
                модератору, сделка будет заморожена до решения.
              </p>
              <textarea
                className={styles.refuseInput}
                placeholder="Что пошло не так? (необязательно)"
                value={refuseText}
                onChange={(e) => setRefuseText(e.target.value)}
                rows={3}
                maxLength={1000}
              />
              <div className={styles.refuseActions}>
                <Button
                  variant="secondary"
                  onClick={() => { haptic.light(); setShowRefuse(false); }}
                >
                  Отмена
                </Button>
                <Button
                  variant="danger"
                  loading={refuse.isPending}
                  onClick={handleRefuse}
                >
                  Отправить жалобу
                </Button>
              </div>
              {refuse.isError && (
                <p className={styles.error}>
                  {(refuse.error as Error)?.message ?? 'Что-то пошло не так'}
                </p>
              )}
            </div>
          )
        )}
      </div>
    );
  }

  return (
    <div className={styles.panel}>
      <p className={styles.heading}>Подтверждение сделки</p>
      <p className={styles.hint}>
        Свяжись с {otherPartyLabel === 'продавец' ? 'продавцом' : 'покупателем'} через
        Telegram и договорись о передаче. После успешной сделки нажми «Подтвердить».
        {otherConfirmed && (
          <strong> {otherPartyLabel === 'продавец' ? 'Продавец' : 'Покупатель'} уже подтвердил.</strong>
        )}
      </p>

      {!showRefuse ? (
        <div className={styles.actions}>
          <Button
            variant="primary"
            fullWidth
            loading={confirm.isPending}
            onClick={handleConfirm}
          >
            Подтвердить сделку
          </Button>
          <button
            className={styles.refuseLink}
            onClick={() => { haptic.light(); setShowRefuse(true); }}
          >
            Проблема со сделкой
          </button>
        </div>
      ) : (
        <div className={styles.refuseBox}>
          <p className={styles.refuseWarning}>
            Если {otherPartyLabel} не выполнил условия — опиши проблему. Жалоба уйдёт
            модератору, сделка будет заморожена до решения.
          </p>
          <textarea
            className={styles.refuseInput}
            placeholder="Что пошло не так? (необязательно)"
            value={refuseText}
            onChange={(e) => setRefuseText(e.target.value)}
            rows={3}
            maxLength={1000}
          />
          <div className={styles.refuseActions}>
            <Button
              variant="secondary"
              onClick={() => { haptic.light(); setShowRefuse(false); }}
            >
              Отмена
            </Button>
            <Button
              variant="danger"
              loading={refuse.isPending}
              onClick={handleRefuse}
            >
              Отправить жалобу
            </Button>
          </div>
        </div>
      )}

      {(confirm.isError || refuse.isError) && (
        <p className={styles.error}>
          {(confirm.error as Error)?.message ??
            (refuse.error as Error)?.message ??
            'Что-то пошло не так'}
        </p>
      )}
    </div>
  );
};

const CheckIcon = () => (
  <div className={styles.checkIcon}>
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="m5 12 5 5L20 7" />
    </svg>
  </div>
);